import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function BillMatch({ txn, onDone }) {
  const [data, setData] = useState(null);
  const [alloc, setAlloc] = useState({});
  const load = () => api.get(`/transactions/${txn.id}/bill-suggestions`).then((r) => {
    setData(r.data);
    setAlloc(Object.fromEntries((txn.bill_allocations || []).map((a) => [a.invoice_id, a.amount])));
  });
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [txn.id]);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (data.message) return <div className="text-sm text-muted-foreground" data-testid="bill-match-message">{data.message}</div>;
  const sum = Object.values(alloc).reduce((a, v) => a + Number(v || 0), 0);
  const save = async (allocations) => {
    try {
      const r = await api.post(`/transactions/${txn.id}/allocate`, { allocations });
      toast.success(`Allocated · on-account remainder ${inr(r.data.on_account)}`);
      onDone?.();
      load();
    } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div className="space-y-5" data-testid="bill-match-panel">
      <div>
        <div className="label-xs mb-2">Suggested matches (confirm before applying)</div>
        {data.suggestions.length === 0 && <div className="text-sm text-muted-foreground">No matching open invoices for this party.</div>}
        {data.suggestions.map((s, i) => (
          <div key={i} className="mb-2 flex items-center justify-between gap-3 rounded-lg border p-3" data-testid="bill-suggestion">
            <div className="min-w-0">
              <div className="text-sm">{s.allocations.map((a) => `${a.invoice_no} (${inr(a.amount)})`).join(" + ")}</div>
              <div className={`text-xs ${s.score >= 0.8 ? "text-emerald-500" : "text-amber-500"}`}>{Math.round(s.score * 100)}% · {s.reason}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => save(s.allocations.map((a) => ({ invoice_id: a.invoice_id, amount: a.amount })))} data-testid="apply-bill-suggestion">Apply</Button>
          </div>
        ))}
      </div>
      <div>
        <div className="label-xs mb-2">Manual allocation · transaction {inr(txn.amount)} · allocated <span className="num">{inr(sum.toFixed(2))}</span></div>
        <div className="space-y-2">
          {data.open_invoices.map((i) => (
            <div key={i.id} className="grid grid-cols-[1fr_auto_120px] items-center gap-3 text-sm">
              <div className="min-w-0 truncate">{i.invoice_no} <span className="text-xs text-muted-foreground">· {fmtDate(i.date)} · {i.kind}</span></div>
              <div className="num text-xs text-muted-foreground">o/s {inr(i.outstanding)}</div>
              <Input className="h-8 num" value={alloc[i.id] || ""} placeholder="0.00" onChange={(e) => setAlloc((s) => ({ ...s, [i.id]: e.target.value }))} data-testid={`alloc-input-${i.invoice_no}`} />
            </div>
          ))}
        </div>
        {data.open_invoices.length > 0 && (
          <Button className="mt-3" size="sm" onClick={() => save(Object.entries(alloc).filter(([, v]) => Number(v) > 0).map(([invoice_id, amount]) => ({ invoice_id, amount })))} data-testid="save-allocation-button">
            Save allocation (remainder goes on-account)
          </Button>
        )}
      </div>
    </div>
  );
}
